import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getDraftPreview, ApiError } from '@/src/infrastructure/lib/api';
import type { ApiInvitation } from '@/src/types';
import { JsonTemplateRenderer } from '@/src/modules/renderer/JsonTemplateRenderer';
import { Button } from '@/src/modules/components/Button';
import { CinematicWeddingExperience } from '@/src/modules/experiences/wedding/CinematicWeddingExperience';
import { TemplateRenderer, registry } from '@/src/modules/templates';
import { Watermark } from '@/src/modules/components/Watermark';
import { Helmet } from 'react-helmet-async';

type PageState =
  | { status: 'loading' }
  | { status: 'ready'; invitation: ApiInvitation }
  | { status: 'expired' }
  | { status: 'deleted' }
  | { status: 'missing'; message: string };

export function DraftPreview() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [state, setState] = useState<PageState>({ status: 'loading' });

  useEffect(() => {
    if (!id || !token) {
      setState({ status: 'missing', message: 'Preview link is invalid or missing token.' });
      return;
    }

    getDraftPreview(id, token)
      .then((invitation) => {
        setState({ status: 'ready', invitation });
      })
      .catch((err) => {
        if (err instanceof ApiError) {
          if (err.status === 410 || err.status === 401) {
            setState({ status: 'expired' });
            return;
          }
          if (err.status === 404) {
            setState({ status: 'deleted' });
            return;
          }
        }
        setState({
          status: 'missing',
          message: err instanceof Error ? err.message : 'Draft not found.',
        });
      });
  }, [id, token]);

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (state.status === 'expired') {
    return (
      <div className="min-h-screen bg-surface-lowest flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-3xl font-bold text-brand-deep mb-3">Preview Expired</h1>
          <p className="text-on-surface-variant mb-6">This preview link has expired or is invalid.</p>
          {id && (
            <Link to={`/wizard?draftId=${encodeURIComponent(id)}`}>
              <Button className="bg-slate-900 text-white hover:bg-slate-800 text-sm whitespace-nowrap">
                Open in Wizard to Edit or Refresh →
              </Button>
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (state.status === 'deleted') {
    return (
      <div className="min-h-screen bg-surface-lowest flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-3xl font-bold text-brand-deep mb-3">Not Found</h1>
          <p className="text-on-surface-variant mb-6">The draft could not be found.</p>
        </div>
      </div>
    );
  }

  if (state.status === 'missing') {
    return (
      <div className="min-h-screen bg-surface-lowest flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-3xl font-bold text-brand-deep mb-3">Preview unavailable</h1>
          <p className="text-on-surface-variant mb-6">{state.message}</p>
        </div>
      </div>
    );
  }

  const { invitation } = state;
  const template = invitation?.template as ApiInvitation['template'] | undefined;

  const renderSharedUI = () => {
    const eventTitle = invitation?.eventName || 'Invitation';
    return (
      <>
        <Helmet>
          <title>{eventTitle} (Draft Preview)</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        
        <div className="fixed top-0 left-0 w-full z-50 bg-amber-300 text-slate-900 py-3 px-4 shadow-md flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="font-medium text-sm sm:text-base">
            📋 DRAFT PREVIEW — This invitation has not been published yet
          </div>
          <Link to={`/wizard?draftId=${encodeURIComponent(invitation?.id || '')}${token ? `&token=${encodeURIComponent(token)}` : ''}`}>
            <Button className="bg-slate-900 text-white hover:bg-slate-800 text-sm whitespace-nowrap">
              Publish This Invitation →
            </Button>
          </Link>
        </div>
        
        <Watermark />
      </>
    );
  };

  const templateMetadata = invitation?.templateId ? registry.get(invitation.templateId)?.metadata : undefined;
  const themeBgColor = templateMetadata?.colorScheme?.bg || (template as any)?.themeConfig?.backgroundColor || null;

  const renderFallbackCard = () => {
    let formattedDate = 'Date to be announced';
    if (invitation?.eventDate) {
      const d = new Date(invitation.eventDate);
      formattedDate = !isNaN(d.getTime()) ? d.toLocaleDateString('en-IN') : String(invitation.eventDate);
    }

    return (
      <div className="min-h-screen bg-surface-lowest py-24 px-4">
        <article className="max-w-xl mx-auto bg-surface-low border border-outline-variant/30 rounded-3xl shadow-xl overflow-hidden text-on-surface">
          <div className="h-2 bg-primary" />
          <div className="p-8 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
              {(invitation?.occasionType || 'EVENT').replace(/_/g, ' ')}
            </p>
            <h1 className="text-4xl font-bold text-brand-deep dark:text-on-surface mt-4">{invitation?.eventName || 'Wedding Invitation'}</h1>
            {invitation?.hostName && (
              <p className="text-on-surface-variant mt-2">Hosted by {invitation.hostName}</p>
            )}
            <div className="mt-8 space-y-3 text-left">
              <p><strong>Date:</strong> {formattedDate}</p>
              <p><strong>Venue:</strong> {invitation?.eventVenue || 'Venue to be announced'}</p>
            </div>
            {invitation?.customMessage && (
              <p className="mt-8 italic text-on-surface-variant">"{invitation.customMessage}"</p>
            )}
          </div>
        </article>
      </div>
    );
  };

  let content;

  if (invitation?.templateId && registry.has(invitation.templateId)) {
    content = (
      <TemplateRenderer templateId={invitation.templateId} data={invitation} hasPartnerAttribution={false} themeBgColor={themeBgColor} />
    );
  } else if (invitation?.occasionType === 'WEDDING') {
    content = (
      <CinematicWeddingExperience data={invitation} hasPartnerAttribution={false} />
    );
  } else if (template?.renderMode === 'json-renderer' && template.layoutSchema) {
    content = (
      <JsonTemplateRenderer
        schema={template.layoutSchema}
        theme={template.themeConfig}
        data={invitation}
        mode="live"
        hasPartnerAttribution={false}
      />
    );
  } else {
    content = renderFallbackCard();
  }

  return (
    <div data-testid="draft-preview-container" style={themeBgColor ? { backgroundColor: themeBgColor } : undefined} className="pt-16">
      {renderSharedUI()}
      <PreviewErrorBoundary fallback={renderFallbackCard()}>
        {content}
      </PreviewErrorBoundary>
    </div>
  );
}

class PreviewErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[DraftPreview] Template rendering failed:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}
